/**
 * ConsumableContent is an abstraction over our Content type that represents
 * content that can be passed to one of our players (e.g. video player, audio
 * player, ereader).
 *
 * This abstraction was initially created to deal with video series, but series
 * are being separated from content. Once that's done "ConsumableContent" will
 * be a pretty hefty abstraction with the sole purpose of dealing with podcast
 * episodes. At that point we can probably boil this abstraction away.
 */
import {
  AudiobookResponse,
  PodcastResponse,
  PodcastEpisodeResponse,
  VideoResponse,
  EbookResponse,
  WebEmbedResponse,
  ContentResponse,
  AlbumResponse,
  MediaUrl,
  MediaImageFile,
  ContentType,
} from "@treefort/api-spec"
import { getAvailableData, isAvailable } from "@treefort/lib/availability"
import { joinContributorNames } from "@treefort/lib/contributor"
import {
  getBestMediaSourceForDownloadingAudio,
  getBestMediaSourceForDownloadingVideo,
  getBestMediaSourceForStreamingAudio,
  getBestMediaSourceForStreamingVideo,
} from "@treefort/lib/media"

import config from "../config"
import { Track } from "./audio-player"
import { getMetadataFromContent } from "./content"
import DownloadItem from "./download-item"
import { stringKeyLookup } from "./i18n/string-key-lookup"

const NO_PROGRESS_CONSUMABLE_CONTENT_TYPES = [
  "webEmbed",
  "album",
] satisfies ConsumableContent["type"][]

// The full episode list is dropped from the item to keep the size down (these
// items are designed to be easily serialized and saved in local storage)
type PodcastWithoutEpisodes = PodcastResponse & {
  details: Omit<PodcastResponse["details"], "episodes">
}

export type PodcastEpisodeConsumableContent = {
  type: "podcastEpisode"
  content: PodcastWithoutEpisodes
  podcastEpisode: PodcastEpisodeResponse
}

export type AudiobookConsumableContent = {
  type: "book"
  content: AudiobookResponse
}

export type VideoConsumableContent = {
  type: "video"
  content: VideoResponse
}

export type EbookConsumableContent = {
  type: "ebook"
  content: EbookResponse
}

export type WebEmbedConsumableContent = {
  type: "webEmbed"
  content: WebEmbedResponse
}

export type AlbumConsumableContent = {
  type: "album"
  content: AlbumResponse
}

export type PlayableContent =
  | PodcastEpisodeConsumableContent
  | AudiobookConsumableContent
  | VideoConsumableContent
  | AlbumConsumableContent

export type ReadableContent = EbookConsumableContent | WebEmbedConsumableContent

export type ConsumableContent = PlayableContent | ReadableContent

export type ConsumableContentForContentResponse<T extends ContentResponse> =
  Extract<ConsumableContent, { type: T["type"] }>

export const getConsumableContentFromPodcastResponse = (
  content: PodcastResponse,
  podcastEpisodeNumber: number,
): PodcastEpisodeConsumableContent => {
  // Find the specific episode we're interested in
  const episode = content.details.episodes.find(
    (episode) => episode.episode === podcastEpisodeNumber,
  )
  if (!episode) {
    throw new Error(
      `Failed to create ConsumableContent: episode "${podcastEpisodeNumber}" not found in podcast "${content.id}"`,
    )
  }

  // Delete the full list of episodes from the podcast
  const detailsWithoutEpisodes = {
    ...content.details,
    episodes: undefined,
  }
  delete detailsWithoutEpisodes.episodes
  const contentWithoutEpisodes = {
    ...content,
    details: detailsWithoutEpisodes,
  } as unknown as PodcastWithoutEpisodes

  return {
    type: "podcastEpisode",
    content: contentWithoutEpisodes,
    podcastEpisode: episode,
  }
}

export const getConsumableContentFromAudiobookResponse = (
  content: AudiobookResponse,
): AudiobookConsumableContent => ({
  type: "book",
  content,
})

export const getConsumableContentFromVideoResponse = (
  content: VideoResponse,
): VideoConsumableContent => ({
  type: "video",
  content,
})

export const getConsumableContentFromEbookResponse = (
  content: EbookResponse,
): EbookConsumableContent => ({
  type: "ebook",
  content,
})

export const getConsumableContentFromWebEmbedResponse = (
  content: WebEmbedResponse,
): WebEmbedConsumableContent => ({
  type: "webEmbed",
  content,
})

export const getConsumableContentFromAlbumResponse = (
  content: AlbumResponse,
): AlbumConsumableContent => ({
  type: "album",
  content,
})

export function getConsumableContentFromContentResponse<
  T extends ContentResponse,
>({
  content,
  podcastEpisodeNumber,
}: {
  content: T
  podcastEpisodeNumber?: number
}) {
  switch (content?.type) {
    case "book":
      return getConsumableContentFromAudiobookResponse(
        content,
      ) as ConsumableContentForContentResponse<T>
    case "ebook":
      return getConsumableContentFromEbookResponse(
        content,
      ) as ConsumableContentForContentResponse<T>
    case "webEmbed":
      return getConsumableContentFromWebEmbedResponse(
        content,
      ) as ConsumableContentForContentResponse<T>
    case "video":
      return getConsumableContentFromVideoResponse(
        content,
      ) as ConsumableContentForContentResponse<T>
    case "podcast": {
      if (podcastEpisodeNumber === undefined) {
        throw new Error(
          `Failed to create ConsumableContent: no episode number provided for podcast "${content.id}"`,
        )
      }
      return getConsumableContentFromPodcastResponse(
        content,
        podcastEpisodeNumber,
      ) as ConsumableContentForContentResponse<T>
    }
    case "album":
      return getConsumableContentFromAlbumResponse(
        content,
      ) as ConsumableContentForContentResponse<T>
    case "videoSeries":
      // HACK: Video series shouldn't even be part of the content system. Remove
      // this as part of the effort to drop video series. #1643
      return undefined
    default:
      content satisfies undefined | never
      return undefined
  }
}

export const getKeyFromConsumableContent = (item: ConsumableContent): string =>
  item.type === "podcastEpisode"
    ? `${item.type}.${item.content.id}.${item.podcastEpisode.episode}`
    : `${item.type}.${item.content.id}`

export const getConsumableContentKeyFromTrack = (track: Track): string =>
  track.extra.contentType === "podcast"
    ? `podcastEpisosde.${track.extra.contentId}.${track.extra.podcastEpisodeNumber}`
    : `${track.extra.contentType}.${track.extra.contentId}`

export function getTrackId(
  contentId: number,
  contentType: ContentType,
  index: number,
) {
  return `${contentType}.${contentId}.${index}`
}

export const getTracksFromConsumableContent = ({
  consumableContent,
  profileId,
}: {
  consumableContent?: ConsumableContent
  profileId: string | null
}): Track[] => {
  if (!consumableContent) {
    return []
  }

  const contentId = consumableContent?.content.id
  const contentType = consumableContent?.content.type
  switch (consumableContent.type) {
    case "podcastEpisode": {
      if (!isAvailable(consumableContent.podcastEpisode.audioMedia)) {
        return []
      }
      const media = consumableContent.podcastEpisode.audioMedia.data
      const artworkMedia =
        getArtworkMediaFromConsumableContent(consumableContent)
      return [
        {
          id: getTrackId(
            contentId,
            contentType,
            consumableContent.podcastEpisode.episode,
          ),
          url: media.url,
          title: consumableContent.podcastEpisode.title,
          artwork: getArtworkUrlFromArtworkMedia(artworkMedia),
          album: consumableContent.content.title,
          extra: {
            contentId: consumableContent.content.id,
            contentType: "podcast",
            contentTitle: consumableContent.content.title,
            contentSku: consumableContent.content.sku ?? undefined,
            urlDownloadKey: DownloadItem.getDownloadKey({
              consumableContent,
              mediaId: media.id,
            }),
            artworkDownloadKey:
              artworkMedia &&
              DownloadItem.getDownloadKey({
                consumableContent,
                mediaId: artworkMedia.id,
              }),
            podcastEpisodeNumber: consumableContent.podcastEpisode.episode,
            profileId,
          },
        },
      ]
    }

    case "book": {
      const audiobookDuration =
        consumableContent.content.details.chapters.reduce((result, chapter) => {
          const data = getAvailableData(chapter.audioMedia)
          return result + (data?.original.duration || 0)
        }, 0)
      let chapterPositionOffset = 0
      return consumableContent.content.details.chapters.flatMap(
        (chapter, chapterIndex, chapters) => {
          const media = getAvailableData(chapter.audioMedia)
          const mediaSource =
            media && getBestMediaSourceForStreamingAudio(media)
          if (!mediaSource) {
            return []
          }
          const artworkMedia =
            getArtworkMediaFromConsumableContent(consumableContent)
          const track = {
            id: getTrackId(contentId, contentType, chapterIndex),
            url: mediaSource.url,
            headers: mediaSource.headers,
            query: mediaSource.query,
            cors: mediaSource.cors,
            duration: mediaSource.duration,
            title: chapter.title,
            artist: joinContributorNames(
              consumableContent.content.contributors,
              "author",
            ),
            album: consumableContent.content.title,
            artwork: getArtworkUrlFromArtworkMedia(artworkMedia),
            extra: {
              contentId: consumableContent.content.id,
              contentType: consumableContent.content.type,
              contentTitle: consumableContent.content.title,
              contentSku: consumableContent.content.sku ?? undefined,
              urlDownloadKey: DownloadItem.getDownloadKey({
                consumableContent,
                mediaId: media.id,
              }),
              artworkDownloadKey:
                artworkMedia &&
                DownloadItem.getDownloadKey({
                  consumableContent,
                  mediaId: artworkMedia.id,
                }),
              profileId,
              audiobookDuration,
              chapterIndex,
              chapterPositionOffset,
              lastChapter: chapterIndex === chapters.length - 1,
            },
          }
          chapterPositionOffset += mediaSource?.duration || 0
          return track
        },
      )
    }

    case "video": {
      const media = getAvailableData(
        consumableContent.content.details.videoMedia,
      )
      const mediaSource = media && getBestMediaSourceForStreamingVideo(media)
      if (!mediaSource) {
        return []
      }
      const artworkMedia =
        getArtworkMediaFromConsumableContent(consumableContent)
      return [
        {
          id: getTrackId(contentId, contentType, 0),
          url: mediaSource.url,
          headers: mediaSource.headers,
          query: mediaSource.query,
          cors: mediaSource.cors,
          duration: mediaSource.duration,
          title: consumableContent.content.title,
          artwork: getArtworkUrlFromArtworkMedia(artworkMedia),
          album: getMetadataFromConsumableContent(consumableContent).subtitle,
          extra: {
            contentId: consumableContent.content.id,
            contentType: consumableContent.content.type,
            contentTitle: consumableContent.content.title,
            contentSku: consumableContent.content.sku ?? undefined,
            urlDownloadKey: DownloadItem.getDownloadKey({
              consumableContent,
              mediaId: media.id,
            }),
            artworkDownloadKey:
              artworkMedia &&
              DownloadItem.getDownloadKey({
                consumableContent,
                mediaId: artworkMedia.id,
              }),
            profileId,
          },
        },
      ]
    }
    case "album": {
      const albumDuration = consumableContent.content.details.tracks.reduce(
        (result, track) => {
          const data = getAvailableData(track.audioMedia)
          return result + (data?.original.duration || 0)
        },
        0,
      )
      let trackPositionOffset = 0
      return consumableContent.content.details.tracks.flatMap(
        (albumTrack, trackIndex, tracks) => {
          const media = getAvailableData(albumTrack.audioMedia)
          const mediaSource =
            media && getBestMediaSourceForStreamingAudio(media)
          if (!mediaSource) {
            return []
          }
          const artworkMedia =
            getArtworkMediaFromConsumableContent(consumableContent)
          const playerTrack = {
            id: getTrackId(contentId, contentType, trackIndex),
            url: mediaSource.url,
            headers: mediaSource.headers,
            query: mediaSource.query,
            cors: mediaSource.cors,
            duration: mediaSource.duration,
            title: albumTrack.title,
            album: consumableContent.content.title,
            artwork: getArtworkUrlFromArtworkMedia(artworkMedia),
            extra: {
              contentId: consumableContent.content.id,
              contentType: consumableContent.content.type,
              contentTitle: consumableContent.content.title,
              contentSku: consumableContent.content.sku ?? undefined,
              urlDownloadKey: DownloadItem.getDownloadKey({
                consumableContent,
                mediaId: media.id,
              }),
              artworkDownloadKey:
                artworkMedia &&
                DownloadItem.getDownloadKey({
                  consumableContent,
                  mediaId: artworkMedia.id,
                }),
              profileId,
              albumDuration,
              trackIndex,
              trackPositionOffset,
              lastTrack: trackIndex === tracks.length - 1,
            },
          }
          trackPositionOffset += mediaSource?.duration || 0
          return playerTrack
        },
      )
    }

    case "ebook":
    case "webEmbed":
      return []

    default:
      consumableContent satisfies undefined | never
      return []
  }
}

export function getArtworkMediaFromConsumableContent(
  consumableContent: ConsumableContent | undefined,
) {
  switch (consumableContent?.type) {
    case "podcastEpisode":
      return consumableContent.podcastEpisode.artworkMedia

    default:
      return consumableContent?.content.artworkMedia
  }
}

function getArtworkUrlFromArtworkMedia(
  media: MediaImageFile | MediaUrl | undefined,
) {
  switch (media?.type) {
    case "imageFile":
      return media.original.url

    case "url":
      return media?.url
  }
}

export const isConsumableContentAvailable = (
  consumableContent?: ConsumableContent,
): boolean => {
  switch (consumableContent?.type) {
    case "podcastEpisode":
      return isAvailable(consumableContent.podcastEpisode.audioMedia)

    case "book":
      return consumableContent.content.details.chapters.some((chapter) =>
        isAvailable(chapter.audioMedia),
      )

    case "video":
      return isAvailable(consumableContent.content.details.videoMedia)

    case "ebook":
      return isAvailable(consumableContent.content.details.ebookMedia)

    case "webEmbed":
      return isAvailable(consumableContent.content.details.webEmbedMedia)

    case "album":
      return consumableContent.content.details.tracks.some((track) =>
        isAvailable(track.audioMedia),
      )

    default:
      consumableContent satisfies undefined | never
      return false
  }
}

/**
 * Returns normalized metadata for any consumable content. Useful for display in
 * lists.
 */
export function getMetadataFromConsumableContent(
  consumableContent: ConsumableContent,
) {
  switch (consumableContent.type) {
    case "podcastEpisode":
      return {
        title: consumableContent.podcastEpisode.title,
        displayTypeStringKey:
          stringKeyLookup.consumableContentType[consumableContent.type],
        subtitle: consumableContent.content.title,
        artwork: getArtworkUrlFromArtworkMedia(
          getArtworkMediaFromConsumableContent(consumableContent),
        ),
        description: consumableContent.podcastEpisode.description,
      }
    default:
      return getMetadataFromContent(consumableContent.content)
  }
}

export const canDownloadConsumableContent = (
  consumableContent: ConsumableContent,
): boolean => {
  if (!config.DOWNLOADS_SUPPORTED) return false
  switch (consumableContent.type) {
    case "podcastEpisode":
      return isAvailable(consumableContent.podcastEpisode.audioMedia)

    case "book": {
      const { chapters } = consumableContent.content.details
      return (
        chapters.length > 0 &&
        chapters.every(
          (chapter) =>
            isAvailable(chapter.audioMedia) &&
            getBestMediaSourceForDownloadingAudio(chapter.audioMedia.data) !==
              undefined,
        )
      )
    }

    case "ebook":
      return isAvailable(consumableContent.content.details.ebookMedia)

    case "video": {
      const { videoMedia } = consumableContent.content.details
      return isAvailable(videoMedia)
        ? getBestMediaSourceForDownloadingVideo(videoMedia.data) !== undefined
        : false
    }

    case "album": {
      const { tracks } = consumableContent.content.details
      return (
        tracks.length > 0 &&
        tracks.every(
          (track) =>
            isAvailable(track.audioMedia) &&
            getBestMediaSourceForDownloadingAudio(track.audioMedia.data) !==
              undefined,
        )
      )
    }

    case "webEmbed":
      return false

    default:
      consumableContent satisfies never
      return false
  }
}

export function shouldShowProgressForConsumableContent(
  consumableContentType: ConsumableContent["type"] | undefined,
): consumableContentType is Exclude<
  ConsumableContent["type"],
  (typeof NO_PROGRESS_CONSUMABLE_CONTENT_TYPES)[number]
> {
  return consumableContentType
    ? !(NO_PROGRESS_CONSUMABLE_CONTENT_TYPES as string[]).includes(
        consumableContentType,
      )
    : false
}

export function getDurationFromConsumableContent(
  consumableContent: ConsumableContent | undefined,
) {
  switch (consumableContent?.type) {
    case "ebook":
      return consumableContent.content.details.pageCount
        ? {
            units: "pages" as const,
            value: consumableContent.content.details.pageCount,
          }
        : undefined

    case "book":
    case "album":
    case "video":
      return consumableContent.content.details.duration
        ? {
            units: "milliseconds" as const,
            value: consumableContent.content.details.duration,
          }
        : undefined

    default:
      return undefined
  }
}
